import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../../ReaduxToolkit/Hooks';
import { collectVariableFieldKeys } from '../fieldUsage';
import { VARIABLE_DRAG_TYPE } from '../Canvas/PageCanvas';
import { fetchMasterFields, SectionFields } from '../masterFields';

export default function DynamicVariablePalette() {
  const clientId = useAppSelector((s) => s.FormDesignerUi.clientId);
  const [data, setData] = useState<SectionFields>({});
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const load = () =>
      fetchMasterFields(clientId)
        .then(setData)
        .catch((err) => console.error('Error loading master fields:', err));
    load();
    // Labels added from the Manage Label tab show up once the user returns here
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [clientId]);

  const doc = useAppSelector((s) => s.FormDocument.present);
  const used = useMemo(() => new Set(collectVariableFieldKeys(doc)), [doc]);
  const total = Object.values(data).reduce((n, list) => n + (Array.isArray(list) ? list.length : 0), 0);
  const usedCount = Object.values(data).reduce(
    (n, list) => n + (Array.isArray(list) ? list.filter((f) => used.has(typeof f === 'object' ? f.field_name : String(f))).length : 0),
    0
  );

  const selectedIds = useAppSelector((s) => s.FormDesignerUi.selectedIds);
  const activeKey = useMemo(() => {
    if (selectedIds.length !== 1) return '';
    for (const page of doc.pages) {
      const el = page.elements.find((e) => e.id === selectedIds[0]);
      if (el) return el.bindings?.dynamicField || '';
    }
    return '';
  }, [doc, selectedIds]);

  // Selecting a bound field on the template brings its label in this list into view
  useEffect(() => {
    if (!activeKey) return;
    const section = Object.entries(data).find(([, list]) =>
      (Array.isArray(list) ? list : []).some((f) => (typeof f === 'object' ? f.field_name : String(f)) === activeKey)
    )?.[0];
    if (section) setCollapsed((c) => (c[section] ? { ...c, [section]: false } : c));
    setQuery('');
    const t = window.setTimeout(() => {
      document.querySelector(`[data-field-key="${CSS.escape(activeKey)}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [activeKey, data]);

  const sections = Object.entries(data)
    .map(([sectionKey, fields]) => {
      const list = Array.isArray(fields) ? fields : [];
      const filtered = list.filter((f) => {
        const name = typeof f === 'object' ? f.field_name : String(f);
        return name.toLowerCase().includes(query.toLowerCase());
      });
      return [sectionKey, filtered] as const;
    })
    .filter(([, fields]) => fields.length > 0);

  return (
    <div className="fd-variable-palette">
      <a
        href="/managelabel"
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary btn-sm w-100 mb-2"
      >
        + Add Label
      </a>
      <input
        type="text"
        className="fd-variable-search form-control form-control-sm"
        placeholder="Search fields..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {total > 0 && (
        <div className="fd-toolbox-hint mb-1">
          {usedCount}/{total} fields placed <span className="fd-variable-legend">highlighted = already on template</span>
        </div>
      )}
      {sections.map(([sectionKey, fields]) => (
        <div key={sectionKey} className="fd-variable-section">
          <div className="fd-variable-section-title" onClick={() => setCollapsed((s) => ({ ...s, [sectionKey]: !s[sectionKey] }))}>
            {collapsed[sectionKey] ? '▸' : '▾'} {sectionKey}
          </div>
          {!collapsed[sectionKey] && (
            <ul className="fd-variable-list">
              {fields.map((f, i) => {
                const fieldName = typeof f === 'object' ? f.field_name : f;
                const instruction = typeof f === 'object' ? f.instruction : '';
                return (
                  <li
                    key={i}
                    data-field-key={fieldName}
                    className={`fd-variable-item${used.has(fieldName) ? ' fd-variable-item-used' : ''}${activeKey === fieldName ? ' fd-variable-item-active' : ''}`}
                    draggable
                    title={instruction}
                    onDragStart={(e) => {
                      e.dataTransfer.setData(VARIABLE_DRAG_TYPE, JSON.stringify({ fieldKey: fieldName, sectionKey }));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                  >
                    {used.has(fieldName) ? '✓' : '•'} {fieldName}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ))}
      {sections.length === 0 && (
        <div className="fd-variable-empty">No fields found</div>
      )}
    </div>
  );
}
